import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Mirrors QUIZ_SEGMENTS in somo-circle1-api's src/constants/quizSegments.js.
// The 'interest' question is reserved: its option values drive report
// lookup and nurture routing, so a value outside this list (or the
// question not being single-select) silently breaks real quiz submissions
// with no error surfaced anywhere in this admin UI until a real user hits
// it. Enforced here, not just hinted at in help text.
const RESERVED_INTEREST_SEGMENTS = ['mental_health', 'financial', 'family', 'all'];

const emptyOption = () => ({
    optionKey: '', label: '', value: '', displayOrder: 0,
});

function QuizQuestionsAdd() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(id ? true : false);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        page: 1,
        questionKey: '',
        questionText: '',
        questionType: 'single-select',
        isRequired: true,
        displayOrder: 0,
        isActive: true,
    });
    const [options, setOptions] = useState([emptyOption()]);

    const fetchQuestionDetails = useCallback(async () => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/questions/${id}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                const question = data.data;
                setFormData({
                    page: question.page,
                    questionKey: question.questionKey,
                    questionText: question.questionText,
                    questionType: question.questionType,
                    isRequired: question.isRequired,
                    displayOrder: question.displayOrder,
                    isActive: question.isActive,
                });
                setOptions(question.options && question.options.length > 0 ? question.options : [emptyOption()]);
            } else {
                toast.error('Failed to fetch quiz question details');
            }
        } catch (error) {
            console.error('Error fetching quiz question:', error);
            toast.error('Error fetching quiz question details');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (id) {
            fetchQuestionDetails();
        }
    }, [id, fetchQuestionDetails]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleOptionChange = (index, field, value) => {
        const updated = [...options];
        updated[index] = { ...updated[index], [field]: value };
        setOptions(updated);
    };

    const addOption = () => {
        setOptions([...options, emptyOption()]);
    };

    const removeOption = (index) => {
        setOptions(options.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.questionKey.trim() || !formData.questionText.trim()) {
            toast.error('Question Key and Question Text are required');
            return;
        }

        const cleanedOptions = options
            .filter((o) => o.optionKey.trim() || o.label.trim() || o.value.trim())
            .map((o) => ({
                optionKey: o.optionKey.trim(),
                label: o.label.trim(),
                value: o.value.trim(),
                displayOrder: parseInt(o.displayOrder, 10) || 0,
            }));

        if ((formData.questionType === 'single-select' || formData.questionType === 'multi-select') && cleanedOptions.length === 0) {
            toast.error('At least one option is required for select-type questions');
            return;
        }

        const normalizedKey = formData.questionKey.trim().toLowerCase();
        if (normalizedKey === 'interest') {
            if (formData.questionType !== 'single-select') {
                toast.error('The "interest" question must be Single select — it drives report lookup and only one segment can be recorded per lead.');
                return;
            }
            const badValues = cleanedOptions
                .map((o) => o.value)
                .filter((v) => !RESERVED_INTEREST_SEGMENTS.includes(v));
            if (badValues.length > 0) {
                toast.error(`"interest" option values must be one of: ${RESERVED_INTEREST_SEGMENTS.join(', ')}. Invalid: ${badValues.join(', ')}`);
                return;
            }
        }

        try {
            setSaving(true);

            const payload = {
                ...formData,
                page: parseInt(formData.page, 10),
                questionKey: formData.questionKey.trim().toLowerCase(),
                questionText: formData.questionText.trim(),
                displayOrder: parseInt(formData.displayOrder, 10) || 0,
                options: cleanedOptions,
            };

            const url = id
                ? `${Base_Url}/admin/quiz/questions/${id}`
                : `${Base_Url}/admin/quiz/questions`;

            const method = id ? 'PUT' : 'POST';

            const res = await authManager.authenticatedFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (data.status === 'success') {
                toast.success(id ? 'Question updated successfully!' : 'Question created successfully!');
                setTimeout(() => navigate('/quizQuestions'), 1500);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (err) {
            console.error('Error saving quiz question:', err);
            toast.error('Error saving quiz question');
        } finally {
            setSaving(false);
        }
    };

    const showOptions = formData.questionType === 'single-select' || formData.questionType === 'multi-select';

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">{id ? 'Edit' : 'Add'} Quiz Question</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/quizQuestions">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Page *</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="page"
                                                            value={formData.page}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value={1}>Page 1</option>
                                                            <option value={2}>Page 2</option>
                                                        </select>
                                                        <small className="text-muted">Which quiz page this renders on</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Question Key *</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="questionKey"
                                                            value={formData.questionKey}
                                                            onChange={handleInputChange}
                                                            disabled={!!id}
                                                            placeholder="e.g., interest, value_importance, branch"
                                                        />
                                                        <small className="text-muted">
                                                            Stable identifier used by the report/routing logic. Cannot be changed once created.
                                                            The key <code>interest</code> is reserved — its option values must be one of:
                                                            mental_health, financial, family, all.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Question Text *</label>
                                                    <div className="col-12 col-md-9">
                                                        <textarea
                                                            className="form-control"
                                                            name="questionText"
                                                            rows="2"
                                                            value={formData.questionText}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., What support matters most to you right now?"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Question Type</label>
                                                    <div className="col-12 col-md-9">
                                                        <select
                                                            className="form-control"
                                                            name="questionType"
                                                            value={formData.questionType}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="single-select">Single select</option>
                                                            <option value="multi-select">Multi select</option>
                                                            <option value="text">Text</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Required</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isRequired"
                                                                name="isRequired"
                                                                checked={formData.isRequired}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isRequired">
                                                                {formData.isRequired ? 'Required to submit the quiz' : 'Optional'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Display Order</label>
                                                    <div className="col-12 col-md-9">
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            name="displayOrder"
                                                            value={formData.displayOrder}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                        <small className="text-muted">Lower numbers appear first on the page</small>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-12 col-md-3 col-form-label">Active</label>
                                                    <div className="col-12 col-md-9">
                                                        <div className="custom-control custom-switch mt-2">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id="isActive"
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleInputChange}
                                                            />
                                                            <label className="custom-control-label" htmlFor="isActive">
                                                                {formData.isActive ? 'Shown in the live quiz' : 'Hidden from the live quiz'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {showOptions && (
                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-3 col-form-label">Options *</label>
                                                        <div className="col-12 col-md-9">
                                                            {options.map((option, index) => (
                                                                <div className="card mb-2" key={index}>
                                                                    <div className="card-body py-2">
                                                                        <div className="row align-items-end">
                                                                            <div className="col-md-3 mb-2">
                                                                                <label className="small mb-1">Option Key</label>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control form-control-sm"
                                                                                    value={option.optionKey}
                                                                                    onChange={(e) => handleOptionChange(index, 'optionKey', e.target.value)}
                                                                                    placeholder="e.g., mental_health"
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-3 mb-2">
                                                                                <label className="small mb-1">Label</label>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control form-control-sm"
                                                                                    value={option.label}
                                                                                    onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                                                                                    placeholder="e.g., Mental health & wellbeing"
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-3 mb-2">
                                                                                <label className="small mb-1">Value</label>
                                                                                <input
                                                                                    type="text"
                                                                                    className="form-control form-control-sm"
                                                                                    value={option.value}
                                                                                    onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                                                                                    placeholder="e.g., mental_health"
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-2 mb-2">
                                                                                <label className="small mb-1">Order</label>
                                                                                <input
                                                                                    type="number"
                                                                                    className="form-control form-control-sm"
                                                                                    value={option.displayOrder}
                                                                                    onChange={(e) => handleOptionChange(index, 'displayOrder', e.target.value)}
                                                                                    min="0"
                                                                                />
                                                                            </div>
                                                                            <div className="col-md-1 mb-2 text-right">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link text-danger p-0"
                                                                                    onClick={() => removeOption(index)}
                                                                                    disabled={options.length === 1}
                                                                                    title="Remove option"
                                                                                >
                                                                                    <i className="fa-solid fa-trash-can" />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm"
                                                                onClick={addOption}
                                                            >
                                                                + Add option
                                                            </button>
                                                            {formData.questionKey.trim().toLowerCase() === 'interest' && (
                                                                <small className="text-muted d-block mt-2">
                                                                    Values for this question must be one of: mental_health, financial, family, all.
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="card-footer text-center">
                                                <button
                                                    type="submit"
                                                    className="btn btn-blue"
                                                    disabled={saving}
                                                >
                                                    {saving ? 'Saving...' : (id ? 'Update' : 'Create')}
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuizQuestionsAdd
